import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { ProjectDto, RoleDto } from '@api/projects/interfaces';
import { UserDto } from '@api/users/interfaces';
import { ProjectUserDto } from '@app/services/project/project.service';
import { ProjectRestService } from '@app/services/rest/project-rest.service';
import { lastValueFrom } from '@app/util/rxjs-7-compat';
import { BehaviorSubject, combineLatest, concat, EMPTY, Observable, of, ReplaySubject, Subject, Subscription } from 'rxjs';
import { catchError, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { select, Store } from '@ngrx/store';
import * as Reducers from '@app/store/reducers';
import { UserRestService } from '@app/services/rest/user-rest.service';
import { HttpErrorResponse } from '@angular/common/http';
import { PermissionService } from '@app/services/permission/permission.service';
import { IUserGroup } from '@interfaces/interfaces';
import { groupBy } from 'lodash';

@Component({
	selector: 'app-roles-edit',
	templateUrl: './roles-edit.component.html',
	styleUrls: ['./roles-edit.component.scss'],
})
export class RolesEditComponent implements OnInit, OnChanges, OnDestroy {

	@Input()
	project: ProjectDto;

	@Input()
	user: UserDto;

	@Input()
	useGlobalRoles: boolean;

	@Input()
	showLabel: 'user' | 'project';

	private readonly project$ = new ReplaySubject<ProjectDto>(1);
	private readonly user$ = new ReplaySubject<UserDto>(1);

	projectUser$: Observable<ProjectUserDto>;

	projectRoles$: Observable<RoleDto[]>;

	globalRoles$: Observable<RoleDto[]>;

	readonly refresh$ = new BehaviorSubject<boolean>(true);

	isActive = false;
	isToggleDisabled = false;

	canSee: boolean;
	canSeeRoles: boolean;
	canEditRoles: boolean;
	canDeleteProjectUser: boolean;
	canAssign: boolean;

	public readonly canSeeGroups$: Observable<boolean>;
	public readonly canEditGroups$: Observable<boolean>;

	private subscription: Subscription = new Subscription();

	private activeUser: UserDto;

	public readonly projectGroups$: Observable<IUserGroup[]>;
	public readonly userGroups$: Observable<string[]>;
	public readonly disabledGroups$: Observable<Record<string, boolean>>;

	public selectedGroups: string[];

	public readonly changedGroups$ = new Subject<string[]>();

	constructor(
			private projectRestService: ProjectRestService,
			private userRestService: UserRestService,
			private permissionService: PermissionService,
			private store: Store,
	) {
		const activeUser$ = this.store.pipe(
			select(Reducers.getUser),
		);

		const activeUserglobalPermissions$ = activeUser$.pipe(
			switchMap(activeUser => this.userRestService.userPermissions$(activeUser.username).pipe(
				catchError(() => of([] as string[])),
			)),
			shareReplay({ bufferSize: 1, refCount: false }),
		);

		const activeUserProjectPermissions$ = combineLatest([activeUser$, this.project$]).pipe(
			switchMap(([activeUser, project]) => this.projectRestService.getProjectUserPermissions$(project.project_id, activeUser.username).pipe(
				catchError(() => of([] as string[])),
			)),
			shareReplay({ bufferSize: 1, refCount: false }),
		);

		const allPermissions$ = combineLatest([activeUserglobalPermissions$, activeUserProjectPermissions$]).pipe(
			map(([globalPermissions, projectPermissions]) => [...globalPermissions, ...projectPermissions]),
		);

		this.projectGroups$ = this.project$.pipe(
			this.permissionService.checkAllPermissionsInObs(allPermissions$, 'user-groups:read'),
			switchMap(project => this.projectRestService.getProjectGroups$(project.project_id).pipe(
				catchError(() => of([])),
			)),
			shareReplay({ bufferSize: 1, refCount: false }),
		);

		let lastValidGroups: string[];

		this.userGroups$ = combineLatest([this.user$, this.project$]).pipe(
			this.permissionService.checkAllPermissionsInObs(allPermissions$, 'user-groups:read'),
			switchMap(([user, project]) => {
				const initialValue$ = this.projectRestService.getProjectUser$(project.project_id, user.username).pipe(
					map((result) => result.group_ids || []),
					catchError((error: unknown) => {
						console.error(error);
						return of<string[]>([]);
					}),
				);
				const updatedValue$ = this.changedGroups$.pipe(
					switchMap(changed => this.projectRestService.updateProjectUser$(this.project.project_id, {
						username: this.user.username,
						group_ids: changed,
					}).pipe(
						map((result) => result.group_ids || []),
						catchError((error: unknown) => {
							console.error(error);
							return lastValidGroups ? of(lastValidGroups) : EMPTY;
						}),
					)),
				);
				return concat(initialValue$, updatedValue$);
			}),
			tap(groups => lastValidGroups = groups),
			shareReplay({ bufferSize: 1, refCount: false }),
		);

		this.disabledGroups$ = combineLatest([this.projectGroups$, this.userGroups$]).pipe(
			map(([projectGroups, userGroups]) => {
				const byTriggerId = groupBy(projectGroups, group => group.triggerId);
				const disabled = {};
				for (const group of projectGroups) {
					disabled[group.id] = group.triggerId && !userGroups.includes(group.id) && byTriggerId[group.triggerId].some(g => userGroups.includes(g.id));
				}
				return disabled;
			})
		);

		this.canSeeGroups$ = this.projectGroups$.pipe(
			map(projectGroups => projectGroups?.length > 0),
		);

		this.canEditGroups$ = this.permissionService.hasAllPermissionsInObs$(allPermissions$, 'project-users:write');

		this.subscription.add(this.userGroups$.subscribe(userGroups => this.selectedGroups = userGroups));
	}

	ngOnInit(): void {
		this.subscription.add(this.store.pipe(
			select(Reducers.getUser),
			tap(state => {
				this.activeUser = state as UserDto;
			}),
			switchMap( (state) => {
				const userPermissions$ = this.userRestService.userPermissions$(state.username);
				if (this.useGlobalRoles) {
					return combineLatest([userPermissions$, of([] as string[])]);
				}
				const projectUserPermissions$ = this.projectRestService.getProjectUserPermissions$(this.project.project_id, this.activeUser.username).pipe(
					catchError(() => {
						return of([] as string[]);
					})
				);
				return combineLatest([userPermissions$, projectUserPermissions$]);
			}),
		).subscribe(([activeUserPermissions, activeProjectUserPermissions]) => {
			this.canSeeRoles = activeProjectUserPermissions.includes('project-users:read') || (activeUserPermissions.includes('users:read') && !!this.useGlobalRoles);
			this.canEditRoles = activeProjectUserPermissions.includes('project-users:write') || (activeUserPermissions.includes('users:write') && !!this.useGlobalRoles);
			this.canDeleteProjectUser = activeProjectUserPermissions.includes('project-users:write');
			this.canAssign = activeProjectUserPermissions.includes('project-users:create') || activeUserPermissions.includes('project-users:create');
		}));
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.project) {
			this.project$.next(this.project);
		}

		if (changes.user) {
			this.user$.next(this.user);
		}

		if (changes.project) {
			if (this.project && !this.useGlobalRoles) {
				this.projectRoles$ = this.projectRestService.getProjectRoles$(this.project.project_id).pipe(
					catchError((error: unknown) => {
						console.log(error);
						return EMPTY;
					}),
				);
			}
		}
		if (changes.user || changes.project || changes.useGlobalRoles) {
			if (this.user && (this.project || this.useGlobalRoles)) {
				this.projectUser$ = this.refresh$.pipe(
					switchMap(() => this.createProjectUserPipe()),
					tap(projectUser => {
						this.isActive = !!projectUser;
						this.isToggleDisabled = this.isActive && !this.canDeleteProjectUser;
					}),
				);
				this.subscription.add(this.projectUser$.subscribe());
			} else {
				this.projectUser$ = EMPTY;
			}
		}
		if (changes.useGlobalRoles) {
			if (this.useGlobalRoles) {
				this.isActive = true;
				this.globalRoles$ = this.projectRestService.getGlobalRoles$().pipe(
					catchError((error: unknown) => {
						console.log(error);
						return EMPTY;
					}),
				);
			}
		}
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

	roleTrackBy(index: number, roleDto: RoleDto): string {
		return roleDto.rolename;
	}

	private createProjectUserPipe(): Observable<ProjectUserDto> {
		if (!this.useGlobalRoles) {
			return this.projectRestService.getProjectUser$(this.project.project_id, this.user.username).pipe(
				catchError((error: unknown) => {
					if ((error as HttpErrorResponse).status !== 404) {
						console.log(error);
					}
					return of(null);
				}),
			);
		} else {
			return of({
				username: this.user.username,
				roles: this.user.roles,
			});
		}
	}

	private refresh() {
		this.refresh$.next(true);
	}

	get label(): string {
		switch (this.showLabel) {
			case 'project':
				if (this.useGlobalRoles)
					return 'Globale Rollen';
				if (!this.project)
					return;
				if (this.project.projectname)
					return `${this.project?.projectname} (${this.project.project_id})`;
				return this.project.project_id;
			case 'user':
				if (!this.user)
					return;
				if (this.user.name)
					return `${this.user.name} (${this.user.username})`;
				return this.user?.username;
		}
	}

	async toggleProjectUser(state: SwitchState): Promise<void> {
		state.loading = true;
		try {
			if (this.project && this.user) {
				const projectUser = await lastValueFrom(this.createProjectUserPipe());

				if (projectUser && this.canDeleteProjectUser) {
					await lastValueFrom(this.projectRestService.deleteProjectUser$(this.project.project_id, this.user.username));
				} else if (this.canAssign) {
					await lastValueFrom(this.projectRestService.assignProjectUser$(this.project.project_id, this.user.username));
				}
			}
			this.refresh();
		} catch (error) {
			console.log(error);
		} finally {
			state.loading = false;
		}
	}

	async setProjectRole(projectRole: RoleDto, enable: boolean, state: SwitchState): Promise<void> {
		state.loading = true;
		try {
			if (this.project && this.user && projectRole) {
				let projectUser = await lastValueFrom(this.createProjectUserPipe());
				if (!projectUser) {
					projectUser = {username: this.user.username, roles: []};
				}
				if (!projectUser.roles) {
					projectUser.roles = [];
				}

				if (enable && !projectUser.roles.includes(projectRole.rolename)) {
					projectUser.roles.push(projectRole.rolename);
					await lastValueFrom(this.projectRestService.updateProjectUser$(this.project.project_id, projectUser));
					this.refresh();
				}
				if (!enable && projectUser.roles.includes(projectRole.rolename)) {
					projectUser.roles.splice(projectUser.roles.indexOf(projectRole.rolename), 1);
					await lastValueFrom(this.projectRestService.updateProjectUser$(this.project.project_id, projectUser));
					this.refresh();
				}
			}
		} catch (error) {
			console.log(error);
		} finally {
			state.loading = false;
		}
	}

	async setGlobalRole(globalRole: string, enable: boolean, state: SwitchState): Promise<void> {
		state.loading = true;
		try {
			if (this.user && globalRole) {
				if (enable && !this.user.roles.includes(globalRole)) {
					this.user.roles.push(globalRole);
					this.user = await lastValueFrom(this.userRestService.updateUser$(this.user));
					this.refresh();
				}
				if (!enable && this.user.roles.includes(globalRole)) {
					this.user.roles.splice(this.user.roles.indexOf(globalRole), 1);
					this.user = await lastValueFrom(this.userRestService.updateUser$(this.user));
					this.refresh();
				}
			}
		} catch (error) {
			console.log(error);
		} finally {
			state.loading = false;
		}
	}

	toggle(event: unknown): void {
		console.log(event);
	}

}

type SwitchState = {
	loading: boolean;
};
